import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Users, FolderOpen, Folder, Sparkles, Mail, Trash2 } from 'lucide-react';
import { apiClient } from '../apiClient';
import Modal from '../components/Modal';
import SelectMenu from '../components/SelectMenu';
import ColorPicker from '../components/ColorPicker';

function TeamPage() {
  const [dashboard, setDashboard] = useState(null);
  const [categories, setCategories] = useState([]);
  const [teamProjects, setTeamProjects] = useState([]);
  const [teamFolders, setTeamFolders] = useState([]);
  const [teamPrompts, setTeamPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [createTeamOpen, setCreateTeamOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [projectOpen, setProjectOpen] = useState(false);
  const [folderOpen, setFolderOpen] = useState(false);
  const [promptOpen, setPromptOpen] = useState(false);

  const [teamName, setTeamName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [busy, setBusy] = useState(false);

  const [projectForm, setProjectForm] = useState({
    name: '',
    description: '',
    category_id: '',
    color: '#3B82F6',
    visibility: 'private',
  });
  const [folderForm, setFolderForm] = useState({
    project_id: '',
    name: '',
    description: '',
    category_id: '',
    color: '#3B82F6',
    visibility: 'private',
  });
  const [promptForm, setPromptForm] = useState({
    project_id: '',
    folder_id: '',
    name: '',
    description: '',
    category_id: '',
    visibility: 'private',
  });

  const sortedCategories = useMemo(
    () => [...(categories || [])].sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [categories]
  );

  const selectedProjectFolders = useMemo(
    () => teamFolders.filter((folder) => folder.project_id === promptForm.project_id),
    [teamFolders, promptForm.project_id]
  );

  const loadAll = async () => {
    setLoading(true);
    setError('');
    try {
      const [dash, cats, projects, folders, prompts] = await Promise.all([
        apiClient.get('/api/team'),
        apiClient.get('/api/categories'),
        apiClient.get('/api/team/projects'),
        apiClient.get('/api/team/folders'),
        apiClient.get('/api/team/prompts'),
      ]);
      setDashboard(dash);
      setCategories(Array.isArray(cats) ? cats : []);
      setTeamProjects(Array.isArray(projects) ? projects : []);
      setTeamFolders(Array.isArray(folders) ? folders : []);
      setTeamPrompts(Array.isArray(prompts) ? prompts : []);
    } catch (err) {
      setError(err.message || 'Unable to load team data.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const canManage = !!dashboard?.can_manage_team;
  const hasTeam = !!dashboard?.team;
  const memberCount = dashboard?.members?.length || 0;
  const remainingSlots = Math.max(0, 5 - Math.max(0, memberCount - 1));

  const createTeam = async () => {
    if (!teamName.trim()) return;
    setBusy(true);
    setError('');
    try {
      await apiClient.post('/api/team', { name: teamName.trim() });
      setCreateTeamOpen(false);
      setTeamName('');
      await loadAll();
    } catch (err) {
      setError(err.message || 'Unable to create team.');
    } finally {
      setBusy(false);
    }
  };

  const inviteMember = async () => {
    if (!inviteEmail.trim()) return;
    setBusy(true);
    setError('');
    try {
      await apiClient.post('/api/team/invite', { email: inviteEmail.trim(), name: inviteName.trim() || null });
      setInviteOpen(false);
      setInviteEmail('');
      setInviteName('');
      await loadAll();
    } catch (err) {
      setError(err.message || 'Unable to invite member.');
    } finally {
      setBusy(false);
    }
  };

  const removeMember = async (userId) => {
    setBusy(true);
    setError('');
    try {
      await apiClient.del(`/api/team/members/${userId}`);
      await loadAll();
    } catch (err) {
      setError(err.message || 'Unable to remove member.');
    } finally {
      setBusy(false);
    }
  };

  const createProject = async () => {
    if (!projectForm.name.trim()) return;
    setBusy(true);
    setError('');
    try {
      await apiClient.post('/api/team/projects', projectForm);
      setProjectOpen(false);
      setProjectForm({ name: '', description: '', category_id: '', color: '#3B82F6', visibility: 'private' });
      await loadAll();
    } catch (err) {
      setError(err.message || 'Unable to create team project.');
    } finally {
      setBusy(false);
    }
  };

  const createFolder = async () => {
    if (!folderForm.name.trim() || !folderForm.project_id) return;
    setBusy(true);
    setError('');
    try {
      await apiClient.post('/api/team/folders', folderForm);
      setFolderOpen(false);
      setFolderForm({ project_id: '', name: '', description: '', category_id: '', color: '#3B82F6', visibility: 'private' });
      await loadAll();
    } catch (err) {
      setError(err.message || 'Unable to create team folder.');
    } finally {
      setBusy(false);
    }
  };

  const createPrompt = async () => {
    if (!promptForm.name.trim() || !promptForm.project_id || !promptForm.folder_id) return;
    setBusy(true);
    setError('');
    try {
      await apiClient.post('/api/team/prompts', promptForm);
      setPromptOpen(false);
      setPromptForm({ project_id: '', folder_id: '', name: '', description: '', category_id: '', visibility: 'private' });
      await loadAll();
    } catch (err) {
      setError(err.message || 'Unable to create team prompt.');
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="min-h-screen p-6"><div className="spinner" /></div>;

  if (!canManage) {
    return (
      <div className="min-h-screen projects-page">
        <div className="card-glass max-w-3xl mx-auto text-center">
          <h1 className="text-3xl font-bold mb-2">Team Workspace</h1>
          <p className="text-gray-300 mb-4">Team management is available on the Pro plan only.</p>
          <p className="text-gray-400 text-sm">Starter: $17/mo | Pro: $27/mo | +$9/mo per new team member account (up to 5)</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen projects-page">
      <div className="page-header">
        <div className="page-header-top">
          <div>
            <h1 className="page-title">Team</h1>
            <p className="page-subtitle">Manage teammates, costs, and team projects/folders/prompts.</p>
          </div>
          {!hasTeam ? (
            <button className="btn-primary" onClick={() => setCreateTeamOpen(true)}>
              <Plus className="w-4 h-4" /> Create Team
            </button>
          ) : (
            <button className="btn-primary" onClick={() => setInviteOpen(true)} disabled={remainingSlots <= 0}>
              <Mail className="w-4 h-4" /> Invite Member ({remainingSlots} left)
            </button>
          )}
        </div>
      </div>

      {error && <div className="error-banner">{error}</div>}

      {hasTeam && (
        <>
          <div className="tile-grid mb-6">
            <div className="tile-card">
              <h3 className="tile-title">{dashboard.team.name}</h3>
              <p className="tile-sub">Team ID: {dashboard.team.team_id}</p>
              <div className="tile-meta">{memberCount} active members</div>
            </div>
            <div className="tile-card">
              <h3 className="tile-title">Estimated Monthly Cost</h3>
              <p className="tile-sub">${dashboard?.cost?.estimated_monthly_total_usd || 37}/month</p>
              <div className="tile-meta">Base ${dashboard?.cost?.base_plan_monthly_usd || 37} + ${dashboard?.cost?.member_addon_monthly_usd || 9} x {dashboard?.cost?.billable_members || 0}</div>
            </div>
          </div>

          <div className="card-glass mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold flex items-center gap-2"><Users className="w-5 h-5" /> Team Members</h2>
            </div>
            <div className="space-y-3">
              {(dashboard.members || []).map((member) => (
                <div key={member.membership_id} className="trash-row">
                  <div className="trash-row-main">
                    <div className="trash-row-title">{member.name || member.email}</div>
                    <div className="trash-row-meta">
                      @{member.username || 'user'} • {member.email} • {member.role}
                      {member.billable ? ' • billable (+$9/mo)' : ''}
                    </div>
                  </div>
                  {member.role !== 'owner' && (
                    <button className="btn-secondary danger btn-compact" onClick={() => removeMember(member.user_id)} disabled={busy}>
                      <Trash2 className="w-4 h-4" /> Remove
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="page-header-actions mb-4">
            <button className="btn-secondary" onClick={() => setProjectOpen(true)}><FolderOpen className="w-4 h-4" /> New Team Project</button>
            <button className="btn-secondary" onClick={() => setFolderOpen(true)} disabled={!teamProjects.length}><Folder className="w-4 h-4" /> New Team Folder</button>
            <button className="btn-secondary" onClick={() => setPromptOpen(true)} disabled={!teamFolders.length}><Sparkles className="w-4 h-4" /> New Team Prompt</button>
          </div>

          <div className="tile-grid mb-6">
            {teamProjects.map((project) => (
              <div key={project.team_project_id} className="tile-card">
                <h3 className="tile-title">{project.name}</h3>
                <p className="tile-sub">{project.description || 'No description.'}</p>
                <div className="tile-meta">Project</div>
              </div>
            ))}
          </div>

          <div className="tile-grid mb-6">
            {teamFolders.map((folder) => (
              <div key={folder.team_folder_id} className="tile-card">
                <h3 className="tile-title">{folder.name}</h3>
                <p className="tile-sub">{folder.description || 'No description.'}</p>
                <div className="tile-meta">Folder</div>
              </div>
            ))}
          </div>

          <div className="tile-grid">
            {teamPrompts.map((prompt) => (
              <div key={prompt.team_prompt_id} className="tile-card">
                <h3 className="tile-title">{prompt.name}</h3>
                <p className="tile-sub">{prompt.description || 'No description.'}</p>
                <div className="tile-meta">Prompt</div>
              </div>
            ))}
          </div>
        </>
      )}

      <Modal
        open={createTeamOpen}
        title="Create Team"
        onClose={() => setCreateTeamOpen(false)}
        footer={<div className="modal-actions"><button className="btn-secondary" onClick={() => setCreateTeamOpen(false)}>Cancel</button><button className="btn-primary" onClick={createTeam} disabled={busy}>Create Team</button></div>}
      >
        <div className="modal-field">
          <label>Team Name</label>
          <input className="input-glass" value={teamName} onChange={(e) => setTeamName(e.target.value)} placeholder="Rebel Team" />
        </div>
      </Modal>

      <Modal
        open={inviteOpen}
        title="Invite Team Member"
        onClose={() => setInviteOpen(false)}
        footer={<div className="modal-actions"><button className="btn-secondary" onClick={() => setInviteOpen(false)}>Cancel</button><button className="btn-primary" onClick={inviteMember} disabled={busy}>Invite</button></div>}
      >
        <div className="modal-field">
          <label>Email</label>
          <input className="input-glass" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} placeholder="user@example.com" />
        </div>
        <div className="modal-field">
          <label>Name (optional)</label>
          <input className="input-glass" value={inviteName} onChange={(e) => setInviteName(e.target.value)} placeholder="Display name" />
        </div>
        <p className="text-sm text-gray-400">Inviting a brand-new email creates a Team Member account and adds +$9/mo modeled cost.</p>
      </Modal>

      <Modal open={projectOpen} title="New Team Project" onClose={() => setProjectOpen(false)} footer={<div className="modal-actions"><button className="btn-secondary" onClick={() => setProjectOpen(false)}>Cancel</button><button className="btn-primary" onClick={createProject} disabled={busy}>Create</button></div>}>
        <div className="modal-field"><label>Name</label><input className="input-glass" value={projectForm.name} onChange={(e) => setProjectForm((p) => ({ ...p, name: e.target.value }))} /></div>
        <div className="modal-field"><label>Description</label><textarea className="input-glass" rows={3} value={projectForm.description} onChange={(e) => setProjectForm((p) => ({ ...p, description: e.target.value }))} /></div>
        <div className="modal-field"><label>Category</label><SelectMenu value={projectForm.category_id} options={sortedCategories.map((c) => ({ value: c.category_id, label: c.name }))} onChange={(value) => setProjectForm((p) => ({ ...p, category_id: value }))} placeholder="Select category" /></div>
        <div className="modal-field"><label>Accent Color</label><ColorPicker value={projectForm.color} onChange={(color) => setProjectForm((p) => ({ ...p, color }))} /></div>
      </Modal>

      <Modal open={folderOpen} title="New Team Folder" onClose={() => setFolderOpen(false)} footer={<div className="modal-actions"><button className="btn-secondary" onClick={() => setFolderOpen(false)}>Cancel</button><button className="btn-primary" onClick={createFolder} disabled={busy}>Create</button></div>}>
        <div className="modal-field"><label>Project</label><SelectMenu value={folderForm.project_id} options={teamProjects.map((p) => ({ value: p.team_project_id, label: p.name }))} onChange={(value) => setFolderForm((p) => ({ ...p, project_id: value }))} placeholder="Select project" /></div>
        <div className="modal-field"><label>Name</label><input className="input-glass" value={folderForm.name} onChange={(e) => setFolderForm((p) => ({ ...p, name: e.target.value }))} /></div>
        <div className="modal-field"><label>Description</label><textarea className="input-glass" rows={3} value={folderForm.description} onChange={(e) => setFolderForm((p) => ({ ...p, description: e.target.value }))} /></div>
        <div className="modal-field"><label>Category</label><SelectMenu value={folderForm.category_id} options={sortedCategories.map((c) => ({ value: c.category_id, label: c.name }))} onChange={(value) => setFolderForm((p) => ({ ...p, category_id: value }))} placeholder="Select category" /></div>
      </Modal>

      <Modal open={promptOpen} title="New Team Prompt" onClose={() => setPromptOpen(false)} footer={<div className="modal-actions"><button className="btn-secondary" onClick={() => setPromptOpen(false)}>Cancel</button><button className="btn-primary" onClick={createPrompt} disabled={busy}>Create</button></div>}>
        <div className="modal-field"><label>Project</label><SelectMenu value={promptForm.project_id} options={teamProjects.map((p) => ({ value: p.team_project_id, label: p.name }))} onChange={(value) => { setPromptForm((p) => ({ ...p, project_id: value, folder_id: '' })); }} placeholder="Select project" /></div>
        <div className="modal-field"><label>Folder</label><SelectMenu value={promptForm.folder_id} options={selectedProjectFolders.map((f) => ({ value: f.team_folder_id, label: f.name }))} onChange={(value) => setPromptForm((p) => ({ ...p, folder_id: value }))} placeholder="Select folder" /></div>
        <div className="modal-field"><label>Name</label><input className="input-glass" value={promptForm.name} onChange={(e) => setPromptForm((p) => ({ ...p, name: e.target.value }))} /></div>
        <div className="modal-field"><label>Description</label><textarea className="input-glass" rows={3} value={promptForm.description} onChange={(e) => setPromptForm((p) => ({ ...p, description: e.target.value }))} /></div>
        <div className="modal-field"><label>Category</label><SelectMenu value={promptForm.category_id} options={sortedCategories.map((c) => ({ value: c.category_id, label: c.name }))} onChange={(value) => setPromptForm((p) => ({ ...p, category_id: value }))} placeholder="Select category" /></div>
      </Modal>
    </div>
  );
}

export default TeamPage;
